import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Divider,
  Tooltip,
  Chip,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Palette as ColorIcon,
  FormatSize as SizeIcon,
  Opacity as OpacityIcon,
  GridOn as GridIcon,
  GridOff as GridOffIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  GroupWork as GroupIcon,
} from '@mui/icons-material';
import { createTemplate, updateTemplate, fetchTemplates } from '../store/slices/templatesSlice';
import WatermarkCanvas from '../components/WatermarkCanvas';
import LayersPanel from '../components/LayersPanel';

const FONT_FAMILIES = [
  'Arial',
  'Arial Black',
  'Helvetica',
  'Times New Roman',
  'Georgia',
  'Courier New',
  'Verdana',
  'Trebuchet MS',
  'Impact',
  'Comic Sans MS',
];

const PRESET_COLORS = [
  '#FFFFFF',
  '#000000',
  '#FF0000',
  '#00FF00',
  '#0000FF',
  '#FFFF00',
  '#FF00FF',
  '#00FFFF',
  '#FFA500',
  '#800080',
];

const PRESET_TEXTS = [
  '{recipient_name} - {unique_id}\nProperty of {company_name}',
  'CONFIDENTIAL - {recipient_name}',
  '{company_name} - Internal Use Only',
  'Copyright © {year} {company_name}',
  'For Review Only - {recipient_email}',
];

function TemplateBuilder() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const { templates } = useSelector((state) => state.templates);
  const { userInfo } = useSelector((state) => state.auth);
  const { activeCompanyId, list: myCompanies } = useSelector((state) => state.company);
  const activeCompany = myCompanies.find((c) => c.id === activeCompanyId);
  const isAdmin = activeCompany?.role === 'admin' || userInfo?.is_superadmin;
  
  const [templateName, setTemplateName] = useState('');
  const [elements, setElements] = useState([
    {
      id: 'element_1',
      name: 'Main Watermark',
      text: '{recipient_name} - {unique_id}\nProperty of {company_name}',
      x: 50,
      y: 10,
      fontSize: 8,  // Changed to percentage (8% of video height = ~86px on 1080p)
      fontFamily: 'Arial',
      color: '#FFFFFF',
      opacity: 0.8,
      visible: true,
      textAlign: 'center',  // 'left', 'center', or 'right'
      rotation: 0,  // Rotation angle in degrees
      groupId: null,  // Group ID if part of a group
    }
  ]);
  const [groups, setGroups] = useState([]); // Array of {id, name, color, expanded}
  const [selectedElement, setSelectedElement] = useState('element_1');
  const [selectedElements, setSelectedElements] = useState([]); // For bulk selection
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // "Moving" watermarks are not implemented; templates are always static.
  const positionType = 'static';
  const [showSafeAreas, setShowSafeAreas] = useState(true);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const updateTimeoutRef = useRef(null);

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);
  
  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        cancelAnimationFrame(updateTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (id && templates.length > 0) {
      const template = templates.find(t => t.id === parseInt(id));
      if (template) {
        setTemplateName(template.name);
        // positionType is hardcoded to 'static'
        
        // Load groups if they exist
        if (template.groups && Array.isArray(template.groups)) {
          setGroups(template.groups);
        } else {
          setGroups([]);
        }
        
        // Convert old format to new format if needed
        if (template.positions && template.positions.length > 0) {
          const convertedElements = template.positions.map((pos, index) => ({
            id: `element_${index + 1}`,
            name: pos.name || `Layer ${index + 1}`,
            text: pos.text || '{recipient_name} - {unique_id}\nProperty of {company_name}',
            x: pos.x || 50,
            y: pos.y || 50,
            fontSize: pos.fontSize || template.font_size || 24,
            fontFamily: pos.fontFamily || template.font_name || 'Arial',
            color: pos.color || template.font_color || '#FFFFFF',
            opacity: pos.opacity || template.opacity || 0.8,
            visible: pos.visible !== undefined ? pos.visible : true,
            textAlign: pos.textAlign || 'center',
            rotation: pos.rotation || 0,
            groupId: pos.groupId || null,
            duration: pos.duration,
          }));
          setElements(convertedElements);
          setSelectedElement(convertedElements[0]?.id);
        }
      }
    }
  }, [id, templates]);

  const currentElement = elements.find(el => el.id === selectedElement);
  const currentGroup = groups.find(g => g.id === selectedElement);
  
  // Automatically select all elements in a group when the group is selected
  useEffect(() => {
    const group = groups.find(g => g.id === selectedElement);
    if (group) {
      // When a group is selected, automatically select all its elements
      setSelectedElements(prevSelected => {
        const groupElementIds = elements
          .filter(el => el.groupId === group.id)
          .map(el => el.id);
        // Only update if the selection actually changed
        if (JSON.stringify(prevSelected) !== JSON.stringify(groupElementIds)) {
          return groupElementIds;
        }
        return prevSelected;
      });
    } else {
      // Clear selection when not selecting a group
      setSelectedElements(prev => prev.length > 0 ? [] : prev);
    }
  }, [selectedElement, groups, elements]);

  const handleElementUpdate = useCallback((elementId, updates) => {
    // Use requestAnimationFrame for position updates to throttle them
    if (updates.x !== undefined || updates.y !== undefined) {
      if (updateTimeoutRef.current) {
        cancelAnimationFrame(updateTimeoutRef.current);
      }
      
      updateTimeoutRef.current = requestAnimationFrame(() => {
        // Check if the selected item is a group
        const group = groups.find(g => g.id === elementId);
        if (group) {
          // Update all elements in the group
          setElements(prev => prev.map(el => 
            el.groupId === group.id ? { ...el, ...updates } : el
          ));
        } else {
          // Update single element
          setElements(prev => prev.map(el => 
            el.id === elementId ? { ...el, ...updates } : el
          ));
        }
      });
    } else {
      // For non-position updates, apply immediately
      const group = groups.find(g => g.id === elementId);
      if (group) {
        // Update all elements in the group
        setElements(prev => prev.map(el => 
          el.groupId === group.id ? { ...el, ...updates } : el
        ));
      } else {
        // Update single element
        setElements(prev => prev.map(el => 
          el.id === elementId ? { ...el, ...updates } : el
        ));
      }
    }
  }, [groups]);

  const handleAddElement = () => {
    const newElement = {
      id: `element_${Date.now()}`,
      name: `Layer ${elements.length + 1}`,
      text: 'New Watermark Text',
      x: 50,
      y: 50,
      fontSize: 8,  // Changed to percentage (8% of video height = ~86px on 1080p)
      fontFamily: 'Arial',
      color: '#FFFFFF',
      opacity: 0.8,
      visible: true,
      textAlign: 'center',
      rotation: 0,
      groupId: null,
    };
    setElements([...elements, newElement]);
    setSelectedElement(newElement.id);
  };
  
  const handleElementsReorder = (reorderedElements) => {
    setElements(reorderedElements);
  };

  const handleDuplicateElement = (elementId) => {
    const element = elementId ? elements.find(el => el.id === elementId) : currentElement;
    if (!element) return;
    
    const newElement = {
      ...element,
      id: `element_${Date.now()}`,
      name: `${element.name} Copy`,
      x: Math.min(element.x + 5, 95),
      y: Math.min(element.y + 5, 95),
    };
    setElements([...elements, newElement]);
    setSelectedElement(newElement.id);
  };

  const handleDeleteElement = () => {
    if (!currentElement || elements.length === 1) return;
    
    setElements(prev => prev.filter(el => el.id !== selectedElement));
    setSelectedElement(elements.find(el => el.id !== selectedElement)?.id);
  };

  const handleToggleVisibility = () => {
    if (!currentElement) return;
    
    handleElementUpdate(selectedElement, { visible: !currentElement.visible });
  };

  const handlePresetText = (preset) => {
    if (!currentElement) return;
    
    handleElementUpdate(selectedElement, { text: preset });
  };

  // Group management functions
  const createGroup = (name) => {
    const newGroup = {
      id: `group_${Date.now()}`,
      name: name || `Group ${groups.length + 1}`,
      color: `#${Math.floor(Math.random()*16777215).toString(16)}`, // Random color
      expanded: true,
    };
    setGroups([...groups, newGroup]);
    return newGroup.id;
  };

  const addToGroup = (elementIds, groupId) => {
    setElements(prev => prev.map(el => 
      elementIds.includes(el.id) ? { ...el, groupId } : el
    ));
  };

  const removeFromGroup = (elementIds) => {
    setElements(prev => prev.map(el => 
      elementIds.includes(el.id) ? { ...el, groupId: null } : el
    ));
  };

  const deleteGroup = (groupId) => {
    setElements(prev => prev.map(el => 
      el.groupId === groupId ? { ...el, groupId: null } : el
    ));
    setGroups(prev => prev.filter(g => g.id !== groupId));
  };

  // Bulk selection functions
  const toggleElementSelection = (elementId) => {
    setSelectedElements(prev => 
      prev.includes(elementId)
        ? prev.filter(id => id !== elementId)
        : [...prev, elementId]
    );
  };

  const selectAll = () => {
    setSelectedElements(elements.map(el => el.id));
  };

  const deselectAll = () => {
    setSelectedElements([]);
  };

  // Bulk edit functions
  const bulkUpdateElements = (updates) => {
    const targetIds = selectedElements.length > 0 ? selectedElements : [selectedElement];
    setElements(prev => prev.map(el => 
      targetIds.includes(el.id) ? { ...el, ...updates } : el
    ));
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    
    if (!templateName) {
      setError('Template name is required');
      return;
    }
    
    if (elements.length === 0) {
      setError('At least one watermark element is required');
      return;
    }
    
    // Convert to backend format
    const templateData = {
      name: templateName,
      position_type: positionType,
      // Use first element's font settings as defaults for backward compatibility
      font_name: elements[0].fontFamily,
      font_size: elements[0].fontSize,
      font_color: elements[0].color,
      opacity: elements[0].opacity,
      // Store groups data
      groups: groups.map(g => ({
        id: g.id,
        name: g.name,
        color: g.color,
        expanded: g.expanded
      })),
      // Store positions with element data
      positions: elements.map(el => ({
        x: el.x,
        y: el.y,
        name: el.name,
        text: el.text,
        fontSize: el.fontSize,
        fontFamily: el.fontFamily,
        color: el.color,
        opacity: el.opacity,
        visible: el.visible,
        textAlign: el.textAlign || 'center',
        rotation: el.rotation || 0,
        groupId: el.groupId || null,
        duration: undefined,
      })),
    };
    
    try {
      if (id) {
        await dispatch(updateTemplate({ id: parseInt(id), ...templateData })).unwrap();
      } else {
        await dispatch(createTemplate(templateData)).unwrap();
      }
      setSuccess(id ? 'Template updated successfully!' : 'Template created successfully!');
      // Add a small delay to ensure state updates
      setTimeout(() => {
        navigate('/settings/templates');
      }, 1000);
    } catch (err) {
      setError(typeof err === 'string' ? err : err.message || 'Failed to save template');
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="warning">
          Only administrators can create or edit watermark templates.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1400px', margin: '0 auto', padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {id ? 'Edit Template' : 'Create Template'}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/settings/templates')}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
          >
            {id ? 'Update' : 'Create'} Template
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Canvas - Full Width */}
        <Grid size={12}>
          <Paper sx={{ p: 2, overflowX: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, minWidth: '960px' }}>
              <TextField
                id="template-name"
                name="templateName"
                label="Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                size="small"
                sx={{ width: 300 }}
              />
              <Stack direction="row" spacing={2}>
                <Tooltip title={showSafeAreas ? "Hide Safe Areas" : "Show Safe Areas"}>
                  <IconButton onClick={() => setShowSafeAreas(!showSafeAreas)} size="small">
                    {showSafeAreas ? <GridIcon /> : <GridOffIcon />}
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            
            <WatermarkCanvas
              elements={elements}
              onElementUpdate={handleElementUpdate}
              selectedElement={selectedElement}
              onSelectElement={setSelectedElement}
              showSafeAreas={showSafeAreas}
            />
          </Paper>
        </Grid>

        {/* Layers Panel */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            {/* Layers Panel */}
            <LayersPanel
              elements={elements}
              groups={groups}
              selectedElement={selectedElement}
              selectedElements={selectedElements}
              bulkEditMode={bulkEditMode}
              onElementsReorder={handleElementsReorder}
              onElementUpdate={handleElementUpdate}
              onSelectElement={setSelectedElement}
              onDeleteElement={handleDeleteElement}
              onToggleElementSelection={toggleElementSelection}
              onSelectAll={selectAll}
              onDeselectAll={deselectAll}
              onCreateGroup={createGroup}
              onAddToGroup={addToGroup}
              onRemoveFromGroup={removeFromGroup}
              onDeleteGroup={deleteGroup}
              onGroupUpdate={(groupId, updates) => {
                setGroups(prev => prev.map(g => 
                  g.id === groupId ? { ...g, ...updates } : g
                ));
              }}
              onToggleBulkEdit={() => setBulkEditMode(!bulkEditMode)}
              onDuplicateElement={handleDuplicateElement}
            />
            
            {/* Add Element Button */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddElement}
            >
              Add New Layer
            </Button>
          </Stack>
        </Grid>
        
        {/* Properties Panel */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {(bulkEditMode || currentGroup) && selectedElements.length > 0 
                  ? currentGroup 
                    ? `Group: ${currentGroup.name} (${selectedElements.length} layers)`
                    : `Bulk Edit (${selectedElements.length} layers)` 
                  : 'Layer Properties'}
              </Typography>
            
            {((bulkEditMode || currentGroup) && selectedElements.length > 0) ? (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {currentGroup 
                    ? `Editing all layers in group "${currentGroup.name}". Changes will apply to all ${selectedElements.length} layers in this group.`
                    : `Editing ${selectedElements.length} selected layers. Changes will apply to all selected layers.`}
                </Alert>
                
                <Divider sx={{ mb: 2 }} />
                
                {/* Bulk Edit Controls */}
                <Stack spacing={3}>
                  {/* Font Family */}
                  <FormControl fullWidth size="small">
                    <InputLabel id="bulk-font-family-label">Font Family</InputLabel>
                    <Select
                      id="bulk-font-family"
                      name="bulkFontFamily"
                      labelId="bulk-font-family-label"
                      value=""
                      label="Font Family"
                      onChange={(e) => bulkUpdateElements({ fontFamily: e.target.value })}
                      displayEmpty
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled><em>Select font</em></MenuItem>
                      {FONT_FAMILIES.map(font => (
                        <MenuItem key={font} value={font}>{font}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Text Alignment */}
                  <FormControl fullWidth size="small">
                    <InputLabel id="bulk-text-align-label">Text Alignment</InputLabel>
                    <Select
                      id="bulk-text-align"
                      name="bulkTextAlign"
                      labelId="bulk-text-align-label"
                      value=""
                      label="Text Alignment"
                      onChange={(e) => bulkUpdateElements({ textAlign: e.target.value })}
                      displayEmpty
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      <MenuItem value="" disabled><em>Select alignment</em></MenuItem>
                      <MenuItem value="left">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AlignLeftIcon fontSize="small" />
                          Left
                        </Box>
                      </MenuItem>
                      <MenuItem value="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AlignCenterIcon fontSize="small" />
                          Center
                        </Box>
                      </MenuItem>
                      <MenuItem value="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AlignRightIcon fontSize="small" />
                          Right
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Font Size */}
                  <Box>
                    <Typography gutterBottom>
                      <SizeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Font Size (Apply to all)
                    </Typography>
                    <Slider
                      id="bulk-font-size"
                      name="bulkFontSize"
                      aria-label="Font Size (Apply to all)"
                      defaultValue={8}
                      onChange={(e, value) => bulkUpdateElements({ fontSize: value })}
                      min={2}
                      max={15}
                      step={0.5}
                      marks={[
                        { value: 2, label: '2%' },
                        { value: 5, label: '5%' },
                        { value: 8, label: '8%' },
                        { value: 10, label: '10%' },
                        { value: 15, label: '15%' },
                      ]}
                    />
                  </Box>

                  {/* Color */}
                  <Box>
                    <Typography gutterBottom>
                      <ColorIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Color (Apply to all)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {PRESET_COLORS.map(color => (
                        <Box
                          key={color}
                          onClick={() => bulkUpdateElements({ color })}
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: color,
                            border: '2px solid #ccc',
                            borderRadius: 0.5,
                            cursor: 'pointer',
                            '&:hover': { transform: 'scale(1.1)' },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Opacity */}
                  <Box>
                    <Typography gutterBottom>
                      <OpacityIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Opacity (Apply to all)
                    </Typography>
                    <Slider
                      id="bulk-opacity"
                      name="bulkOpacity"
                      aria-label="Opacity (Apply to all)"
                      defaultValue={0.8}
                      onChange={(e, value) => bulkUpdateElements({ opacity: value })}
                      min={0.01}
                      max={1}
                      step={0.01}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                      marks={[
                        { value: 0.01, label: '1%' },
                        { value: 0.5, label: '50%' },
                        { value: 1, label: '100%' },
                      ]}
                    />
                  </Box>

                  {/* Rotation */}
                  <Box>
                    <Typography gutterBottom>
                      Rotation (Apply to all)
                    </Typography>
                    <Slider
                      id="bulk-rotation"
                      name="bulkRotation"
                      aria-label="Rotation (Apply to all)"
                      defaultValue={0}
                      onChange={(e, value) => bulkUpdateElements({ rotation: value })}
                      min={-180}
                      max={180}
                      step={1}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}°`}
                      marks={[
                        { value: -180, label: '-180°' },
                        { value: 0, label: '0°' },
                        { value: 180, label: '180°' },
                      ]}
                    />
                  </Box>

                  {/* Visibility */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => bulkUpdateElements({ visible: true })}
                    >
                      Show All
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityOffIcon />}
                      onClick={() => bulkUpdateElements({ visible: false })}
                    >
                      Hide All
                    </Button>
                  </Box>

                  {/* Group Actions */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<GroupIcon />}
                      onClick={() => {
                        const groupId = createGroup();
                        addToGroup(selectedElements, groupId);
                      }}
                    >
                      Group Selected
                    </Button>
                  </Box>
                </Stack>
              </>
            ) : currentElement && (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Tooltip title={currentElement.visible ? 'Hide' : 'Show'}>
                    <IconButton onClick={handleToggleVisibility} size="small">
                      {currentElement.visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </Tooltip>
                  {elements.length > 1 && (
                    <Tooltip title="Delete this layer from the template" arrow placement="top">
                      <IconButton onClick={handleDeleteElement} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Text Content */}
                <Box sx={{ mb: 2, width: '100%' }}>
                  <Tooltip title="Enter the watermark text with dynamic placeholders" arrow placement="top">
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Text Content
                    </Typography>
                  </Tooltip>
                  <Box sx={{ width: '100%', position: 'relative' }}>
                    <textarea
                      id="element-text"
                      name="elementText"
                      aria-label="Text Content"
                      value={currentElement.text}
                      onChange={(e) => handleElementUpdate(selectedElement, { text: e.target.value })}
                      style={{
                        width: '100%',
                        height: '120px',
                        minHeight: '80px',
                        maxHeight: '300px',
                        minWidth: '100%',
                        maxWidth: '100%',
                        resize: 'both',
                        padding: '10px',
                        fontSize: '14px',
                        fontFamily: 'Arial, sans-serif',
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: '4px',
                        backgroundColor: '#ffffff',
                        color: '#000000',
                        boxSizing: 'border-box',
                        display: 'block',
                        overflow: 'auto',
                        lineHeight: '1.5',
                        margin: 0,
                      }}
                    />
                  </Box>
                  <Tooltip title="These placeholders will be replaced with actual values when the watermark is applied" arrow placement="top">
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Use {'{recipient_name}'}, {'{unique_id}'}, {'{company_name}'}, {'{year}'}, {'{recipient_email}'}
                    </Typography>
                  </Tooltip>
                </Box>

                {/* Preset Texts */}
                <Box sx={{ mb: 2 }}>
                  <Tooltip title="Click to use pre-made watermark text templates" arrow placement="top">
                    <Typography variant="caption" color="text.secondary">
                      Quick Templates:
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {PRESET_TEXTS.map((preset, index) => (
                      <Tooltip key={index} title={preset} arrow placement="top">
                        <Chip
                          label={`Template ${index + 1}`}
                          size="small"
                          onClick={() => handlePresetText(preset)}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Font Family */}
                <Tooltip title="Choose the font style for this watermark layer" arrow placement="top">
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="element-font-family-label">Font Family</InputLabel>
                    <Select
                      id="element-font-family"
                      name="elementFontFamily"
                      labelId="element-font-family-label"
                      value={currentElement.fontFamily}
                      label="Font Family"
                      onChange={(e) => handleElementUpdate(selectedElement, { fontFamily: e.target.value })}
                    >
                      {FONT_FAMILIES.map(font => (
                        <MenuItem key={font} value={font}>{font}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>

                {/* Text Alignment */}
                <Tooltip title="Align text within its bounding box" arrow placement="top">
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="element-text-align-label">Text Alignment</InputLabel>
                    <Select
                      id="element-text-align"
                      name="elementTextAlign"
                      labelId="element-text-align-label"
                      value={currentElement.textAlign || 'center'}
                      label="Text Alignment"
                      onChange={(e) => handleElementUpdate(selectedElement, { textAlign: e.target.value })}
                    >
                      <MenuItem value="left">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AlignLeftIcon fontSize="small" />
                          Left
                        </Box>
                      </MenuItem>
                      <MenuItem value="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AlignCenterIcon fontSize="small" />
                          Center
                        </Box>
                      </MenuItem>
                      <MenuItem value="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AlignRightIcon fontSize="small" />
                          Right
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Tooltip>

                {/* Font Size */}
                <Box sx={{ mt: 2 }}>
                  <Tooltip title="Font size as percentage of video height" arrow placement="top">
                    <Typography gutterBottom>
                      <SizeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Font Size: {currentElement.fontSize}% ({Math.round(currentElement.fontSize * 10.8)}px on 1080p)
                    </Typography>
                  </Tooltip>
                  <Slider
                    id="element-font-size"
                    name="elementFontSize"
                    aria-label="Font Size"
                    value={currentElement.fontSize}
                    onChange={(e, value) => handleElementUpdate(selectedElement, { fontSize: value })}
                    min={2}
                    max={15}
                    step={0.5}
                    marks={[
                      { value: 2, label: '2%' },
                      { value: 5, label: '5%' },
                      { value: 8, label: '8%' },
                      { value: 10, label: '10%' },
                      { value: 15, label: '15%' },
                    ]}
                  />
                </Box>

                {/* Color */}
                <Box sx={{ mt: 3 }}>
                  <Tooltip title="Set the text color for this watermark layer" arrow placement="top">
                    <Typography gutterBottom>
                      <ColorIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Color
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Click to choose a custom color" arrow placement="top">
                      <TextField
                        id="element-color"
                        name="elementColor"
                        type="color"
                        value={currentElement.color}
                        onChange={(e) => handleElementUpdate(selectedElement, { color: e.target.value })}
                        sx={{ width: 80 }}
                        aria-label="Color picker"
                      />
                    </Tooltip>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {PRESET_COLORS.map(color => (
                        <Box
                          key={color}
                          onClick={() => handleElementUpdate(selectedElement, { color })}
                          sx={{
                            width: 24,
                            height: 24,
                            bgcolor: color,
                            border: '1px solid #ccc',
                            borderRadius: 0.5,
                            cursor: 'pointer',
                            '&:hover': { transform: 'scale(1.1)' },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>

                {/* Opacity */}
                <Box sx={{ mt: 3 }}>
                  <Tooltip title="Control how transparent the watermark appears" arrow placement="top">
                    <Typography gutterBottom>
                      <OpacityIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      Opacity: {Math.round(currentElement.opacity * 100)}%
                    </Typography>
                  </Tooltip>
                  <Slider
                    id="element-opacity"
                    name="elementOpacity"
                    aria-label="Opacity"
                    value={currentElement.opacity}
                    onChange={(e, value) => handleElementUpdate(selectedElement, { opacity: value })}
                    min={0.01}
                    max={1}
                    step={0.01}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                    marks={[
                      { value: 0.01, label: '1%' },
                      { value: 0.25, label: '25%' },
                      { value: 0.5, label: '50%' },
                      { value: 0.75, label: '75%' },
                      { value: 1, label: '100%' },
                    ]}
                  />
                </Box>

                {/* Position */}
                <Box sx={{ mt: 3 }}>
                  <Tooltip title="Set the watermark position as percentage of video dimensions" arrow placement="top">
                    <Typography gutterBottom>Position</Typography>
                  </Tooltip>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Tooltip title="Horizontal position (0% = left edge, 100% = right edge)" arrow placement="top">
                        <TextField
                          id="element-x-position"
                          name="elementXPosition"
                          fullWidth
                          label="X Position (%)"
                          type="number"
                          value={Math.round(currentElement.x)}
                          onChange={(e) => handleElementUpdate(selectedElement, { 
                            x: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) 
                          })}
                          InputProps={{ inputProps: { min: 0, max: 100 } }}
                        />
                      </Tooltip>
                    </Grid>
                    <Grid size={6}>
                      <Tooltip title="Vertical position (0% = top edge, 100% = bottom edge)" arrow placement="top">
                        <TextField
                          id="element-y-position"
                          name="elementYPosition"
                          fullWidth
                          label="Y Position (%)"
                          type="number"
                          value={Math.round(currentElement.y)}
                          onChange={(e) => handleElementUpdate(selectedElement, { 
                          y: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) 
                        })}
                        InputProps={{ inputProps: { min: 0, max: 100 } }}
                        />
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>

                {/* Rotation */}
                <Box sx={{ mt: 3 }}>
                  <Typography gutterBottom>
                    Rotation: {currentElement.rotation || 0}°
                  </Typography>
                  <Slider
                    id="element-rotation"
                    name="elementRotation"
                    aria-label="Rotation"
                    value={currentElement.rotation || 0}
                    onChange={(e, value) => handleElementUpdate(selectedElement, { rotation: value })}
                    min={-180}
                    max={180}
                    step={1}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value}°`}
                    marks={[
                      { value: -180, label: '-180°' },
                      { value: -90, label: '-90°' },
                      { value: 0, label: '0°' },
                      { value: 90, label: '90°' },
                      { value: 180, label: '180°' },
                    ]}
                  />
                </Box>

              </>
            )}
            </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default TemplateBuilder;